/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import type { ReactNode } from 'react';

import { LAYOUT2 } from '@/Client/Styles';

import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import { RightIcon } from '@kaiizen/kai-shiki/react';
import { AIScanVideoUploadTriggerButton } from '../AIScanVideoUploadModal/AIScanVideoUploadTriggerButton';
import {
  type FocusPanelScopeLevel,
  getScanFocusPanelTab,
  PROGRESS_ALT_PAGE,
  type ScanFocusPanelPage,
} from './scanFocusPanelTypes';

/**
 * How far a panel's content sits in from the panel's own edges.
 *
 * One number, exported, because the breadcrumb and the editor's stacked modules are drawn by
 * different files and the reader sees them as one column: any two values here read as a module
 * that failed to line up rather than as two independent paddings.
 */
export const FOCUS_PANEL_INDENT_PX = LAYOUT2.spacing3;

/**
 * The tabs, in the order they are read left to right — one list rather than an order and a lookup, so
 * adding a tab or taking one away is one edit in one place.
 *
 * The panel the redesign replaced, `photos` (Gallery), is deliberately absent. Its page still
 * answers to its route, so this list is the only thing standing between it and
 * the tab row: comparing old against new means typing the URL, and putting a tab back is one line.
 */
const SCAN_FOCUS_PANEL_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'edit', label: 'Editor' },
  { id: PROGRESS_ALT_PAGE, label: 'Progress' },
  { id: 'measurements', label: 'Measurements' },
  { id: 'artifact', label: 'Artifact' },
] as const;

const WORKSPACE_STYLE = css`
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TABS_STYLE = css`
  flex: 0 0 44px;
  display: flex;
  align-items: stretch;
  gap: 22px;
  padding: 0 18px;
  border-bottom: 1px solid ${COLORS.stroke};
  background: ${COLORS.white};
`;

const TABS_TRAILING_STYLE = css`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

const TAB_BASE_STYLE = css`
  height: 100%;
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const TAB_ACTIVE_STYLE = css`
  ${TAB_BASE_STYLE};
  border-bottom-color: ${COLORS.ink};
  color: ${COLORS.ink};
  cursor: default;
`;

const TAB_INACTIVE_STYLE = css`
  ${TAB_BASE_STYLE};
  color: ${COLORS.textTertiary};
  cursor: pointer;

  &:hover {
    color: ${COLORS.ink};
  }
`;

export const BODY_STYLE = css`
  label: BODY_STYLE;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${LAYOUT2.spacing2}px ${FOCUS_PANEL_INDENT_PX}px 80px;
`;

const BODY_FLUSH_STYLE = css`
  label: BODY_FLUSH_STYLE;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
`;

const BREADCRUMB_STYLE = css`
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 18px;
  color: ${COLORS.ink};
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

/**
 * A flush body scrolls its child, so the trail supplies the padding a flush body doesn't and holds its
 * height against the scroll region below it.
 *
 * It gives back the bottom margin: the first module below a flush body brings its own top padding,
 * and the two together read as one gap too many.
 */
const BREADCRUMB_FLUSH_STYLE = css`
  ${BREADCRUMB_STYLE};
  flex-shrink: 0;
  margin-bottom: 0;
  padding: ${LAYOUT2.spacing2}px ${FOCUS_PANEL_INDENT_PX}px 0;
`;

const BREADCRUMB_BUTTON_STYLE = css`
  border: 0;
  padding: 0;
  background: transparent;
  color: ${COLORS.textTertiary};
  font: inherit;
  cursor: pointer;
  text-underline-offset: 3px;
  text-transform: uppercase;
  &:hover {
    color: ${COLORS.ink};
    text-decoration: underline;
  }
`;

export const SCOPE_TITLE = 'Scope';

/**
 * Where in the scan the panel is looking, and the way back out of it.
 *
 * Drawn at every scope, all-scope included, so the reader always has the same line in the same
 * place telling them where they are — a trail that comes and goes is a row of modules that shifts
 * up and down as you move around.
 *
 * A contractor route carries `contractorTitle` instead of `groupTitle` — there is no real group
 * being browsed there (see `AdminScanPageNew`'s `group` resolution), only a contractor bucket, so
 * `scopeLevel` itself never reaches `'group'`/`'task'` on that route. The trail and its middle
 * segment key off `contractorTitle`/`taskTitle` directly instead of `scopeLevel` so the contractor
 * name still shows and is still clickable back to that contractor's full task list.
 */
const ScopeBreadcrumb = ({
  scopeLevel,
  groupTitle,
  taskTitle,
  selectedGroupId,
  contractorTitle,
  selectedContractorId,
  onSelectAll,
  onSelectGroup,
  onSelectContractor,
  flush,
}: {
  scopeLevel: FocusPanelScopeLevel;
  groupTitle?: string;
  taskTitle?: string;
  selectedGroupId?: string;
  contractorTitle?: string;
  selectedContractorId?: string;
  onSelectAll?: () => void;
  onSelectGroup?: (groupId: string) => void;
  onSelectContractor?: (contractorId: string) => void;
  flush?: boolean;
}) => (
  <nav css={flush ? BREADCRUMB_FLUSH_STYLE : BREADCRUMB_STYLE} aria-label={'Focus panel scope'}>
    {scopeLevel === 'scope' && !contractorTitle ? null : (
      <>
        <button type={'button'} css={BREADCRUMB_BUTTON_STYLE} onClick={onSelectAll}>{SCOPE_TITLE}</button>
        <RightIcon aria-hidden={'true'} size={12} />
        {contractorTitle
          ? (taskTitle && onSelectContractor && selectedContractorId
            ? (
              <button
                type={'button'}
                css={BREADCRUMB_BUTTON_STYLE}
                onClick={() => onSelectContractor(selectedContractorId)}
              >
                {contractorTitle}
              </button>
            )
            : <span>{contractorTitle}</span>)
          : scopeLevel === 'task' && onSelectGroup && selectedGroupId
          ? (
            <button
              type={'button'}
              css={BREADCRUMB_BUTTON_STYLE}
              onClick={() => onSelectGroup(selectedGroupId)}
            >
              {groupTitle}
            </button>
          )
          : <span>{groupTitle}</span>}
      </>
    )}
    {taskTitle
      ? (
        <>
          <RightIcon aria-hidden={'true'} size={12} />
          <span>{taskTitle}</span>
        </>
      )
      : null}
  </nav>
);

export const FocusPanelNewVideoButton = ({
  activeCount = 0,
  onClick,
}: {
  activeCount?: number;
  onClick: () => void;
}) => (
  <AIScanVideoUploadTriggerButton
    variant={'focusPanelChip'}
    activeCount={activeCount}
    onClick={onClick}
  />
);

/** Work-mode title tab row and optional scope breadcrumb for focused scan panels. */
export const FocusPanelTitlebar = ({
  activePanel,
  onSelectPanel,
  scopeLevel,
  showProjectOverview = false,
  groupTitle,
  taskTitle,
  selectedGroupId,
  contractorTitle,
  selectedContractorId,
  onSelectAll,
  onSelectGroup,
  onSelectContractor,
  bodyVariant = 'default',
  hideBreadcrumb = false,
  trailing,
  children,
}: {
  activePanel: ScanFocusPanelPage;
  onSelectPanel: (panel: ScanFocusPanelPage) => void;
  scopeLevel: FocusPanelScopeLevel;
  showProjectOverview?: boolean;
  groupTitle?: string;
  taskTitle?: string;
  selectedGroupId?: string;
  contractorTitle?: string;
  selectedContractorId?: string;
  onSelectAll?: () => void;
  onSelectGroup?: (groupId: string) => void;
  onSelectContractor?: (contractorId: string) => void;
  bodyVariant?: 'default' | 'flush';
  /** Full-bleed workspaces (e.g. the walk comparison) omit the scope breadcrumb. */
  hideBreadcrumb?: boolean;
  /** Right-aligned slot in the tab row (e.g. the Progress photos-only toggle). */
  trailing?: ReactNode;
  children?: ReactNode;
}) => {
  const activeTab = getScanFocusPanelTab(activePanel);
  return (
    <div css={WORKSPACE_STYLE} data-focus-panel={activePanel}>
      <div css={TABS_STYLE} role={'tablist'} aria-label={'Focused content modes'}>
        {SCAN_FOCUS_PANEL_TABS.filter(panel => panel.id !== 'overview' || showProjectOverview).map(panel => {
          const isActive = panel.id === activeTab;
          return (
            <button
              key={panel.id}
              type={'button'}
              role={'tab'}
              aria-selected={isActive}
              css={isActive ? TAB_ACTIVE_STYLE : TAB_INACTIVE_STYLE}
              onClick={isActive ? undefined : () => onSelectPanel(panel.id)}
            >
              {panel.label}
            </button>
          );
        })}
        {trailing ? <span css={TABS_TRAILING_STYLE}>{trailing}</span> : null}
      </div>
      <div css={bodyVariant === 'flush' ? BODY_FLUSH_STYLE : BODY_STYLE}>
        {hideBreadcrumb
          ? null
          : (
            <ScopeBreadcrumb
              {...{
                scopeLevel,
                groupTitle,
                taskTitle,
                selectedGroupId,
                contractorTitle,
                selectedContractorId,
                onSelectAll,
                onSelectGroup,
                onSelectContractor,
              }}
              flush={bodyVariant === 'flush'}
            />
          )}
        {children}
      </div>
    </div>
  );
};
